import React, { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../constants/brand";
import { http } from "../context/AuthContext";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/contact", form);
      toast.success("Message sent! We'll reply within 2 hours.");
      setForm({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error("Couldn't send — please try WhatsApp");
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const msg = `Hi EarlyPrint, I'm ${form.name || "a customer"}. ${form.message || "I have a question."}`;
    window.open(`https://wa.me/919899747172?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <section className="border-b border-neutral-200 bg-neutral-950 text-white grain">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">Contact</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl" data-testid="contact-title">
            Have a question? <span className="text-lime-400">Talk to us.</span>
          </h1>
          <p className="mt-6 max-w-xl text-neutral-300">Reach us on WhatsApp, by phone, or send a message — we reply within 2 hours.</p>
        </div>
      </section>

      <section className="border-b border-neutral-200">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-12 md:px-6 md:py-24">
          <div className="md:col-span-5">
            <div className="space-y-4">
              <a href={BRAND.social.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 rounded-lg border border-neutral-200 bg-white p-5 hover:border-lime-500" data-testid="contact-whatsapp-card">
                <MessageCircle className="text-[#25D366]" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">WhatsApp</div>
                  <div className="font-display text-lg font-bold">{BRAND.phoneIntl}</div>
                  <div className="text-sm text-neutral-600">Fastest way to reach us</div>
                </div>
              </a>
              <a href={`tel:${BRAND.phone}`} className="flex items-start gap-4 rounded-lg border border-neutral-200 bg-white p-5 hover:border-lime-500" data-testid="contact-phone-card">
                <Phone className="text-lime-500" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Phone</div>
                  <div className="font-display text-lg font-bold">{BRAND.phoneIntl}</div>
                  <div className="text-sm text-neutral-600">Mon–Sat · 9AM to 9PM</div>
                </div>
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 rounded-lg border border-neutral-200 bg-white p-5 hover:border-lime-500" data-testid="contact-email-card">
                <Mail className="text-lime-500" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Email</div>
                  <div className="font-display text-lg font-bold">{BRAND.email}</div>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-lg border border-neutral-200 bg-white p-5">
                <MapPin className="text-lime-500" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Location</div>
                  <div className="font-display text-lg font-bold">{BRAND.location}</div>
                  <div className="text-sm text-neutral-600">Serving all of Delhi NCR</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7" data-testid="contact-form">
            <div className="rounded-lg border border-neutral-200 bg-white p-6 md:p-8">
              <h2 className="font-display text-2xl font-bold">Send us a message</h2>
              <p className="mt-1 text-sm text-neutral-600">Fill this in and we'll get back on WhatsApp or email — usually within 2 hours.</p>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <Field label="Full name" value={form.name} onChange={onChange("name")} required testid="contact-name" />
                <Field label="Email" type="email" value={form.email} onChange={onChange("email")} required testid="contact-email" />
                <Field label="Phone (optional)" value={form.phone} onChange={onChange("phone")} testid="contact-phone" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Subject</label>
                  <select value={form.subject} onChange={onChange("subject")} className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none ring-lime-400 focus:border-neutral-900 focus:ring-2" data-testid="contact-subject">
                    <option>General enquiry</option>
                    <option>Bulk order</option>
                    <option>Vendor / print shop</option>
                    <option>Support / issue</option>
                    <option>Partnership</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Message</label>
                <textarea value={form.message} onChange={onChange("message")} rows={5} required className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none ring-lime-400 focus:border-neutral-900 focus:ring-2" data-testid="contact-message" />
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button type="submit" disabled={submitting} className="btn-offset inline-flex items-center gap-2 rounded-md bg-lime-400 px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300 disabled:opacity-60" data-testid="contact-submit">
                  {submitting ? "Sending…" : (<>Send message <Send size={16} /></>)}
                </button>
                <button type="button" onClick={openWhatsApp} className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-semibold hover:border-neutral-900" data-testid="contact-whatsapp-btn">Chat on WhatsApp instead</button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">{label}</label>
      <input {...props} data-testid={testid} className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm outline-none ring-lime-400 focus:border-neutral-900 focus:ring-2" />
    </div>
  );
}
